import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {RootState} from '@src/redux/store';
import {BASE_URL} from '@src/helpers/constants';
import {retrieveItem} from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  query getReportOptions($filter: reportFilterInput) {
    getReportOptions(filter: $filter) {
      rootLevelList {
        id
        name
        levelId
        levelName
      }
      subjectFieldList {
        key
        label
        isDefault
        isSubjectKey
      }
      questionList {
        id
        name
        shortQuestion
        groupId
        groupName
        orderNumber
        isDefault
      }
      shortQuestionMaxLength
      error {
        message
        statusCode
      }
    }
  }
`;

export type GetReportOptionsParams = {
  filter?: TYPES.ReportFilterInput;
};

const initialState: TYPES.ReportOptionsState = {
  result: null,
  error: null,
  loading: false,
};

export const getReportOptionsRequest = createAsyncThunk<
  TYPES.ReportOptionsResponse,
  GetReportOptionsParams,
  {extra: ApolloClient<NormalizedCacheObject>}
>('report/get_options', async (params: GetReportOptionsParams) => {
  const sessionId = retrieveItem('sessionId') ?? '';
  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        sessionId: sessionId,
      },
      body: JSON.stringify({
        operationName: 'getReportOptions',
        query: GRAPHQL_QUERY,
        variables: params ?? {},
      }),
    });
    const json = await response.json();
    if (json?.errors?.length) {
      const first = json.errors[0];
      return Promise.reject({
        message: first.message || 'Failed to load report options',
        statusCode: String(response.status),
      });
    }
    return json?.data?.getReportOptions as TYPES.ReportOptionsResponse;
  } catch (error) {
    const err = error as {message?: string; statusCode?: string};
    return Promise.reject({
      message: err.message || 'An error occurred',
      statusCode: err.statusCode || '500',
    });
  }
});

const getReportOptionsSlice = createSlice({
  name: 'get_report_options',
  initialState: initialState,
  reducers: {
    getReportOptionsClear: state => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getReportOptionsRequest.pending, state => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getReportOptionsRequest.fulfilled, (state, action) => {
        state.result = action.payload;
        state.loading = false;
      })
      .addCase(getReportOptionsRequest.rejected, (state, action) => {
        state.error =
          (action.payload as TYPES.Error) ||
          {message: 'Failed to fetch', statusCode: '500'};
        state.loading = false;
      });
  },
});

export const {getReportOptionsClear} = getReportOptionsSlice.actions;

export const getReportOptions = createSelector(
  (state: RootState) => state.report.getReportOptions.result,
  (state: RootState) => state.report.getReportOptions.error,
  (state: RootState) => state.report.getReportOptions.loading,
  (result, error, loading) => ({result, error, loading}),
);

export default getReportOptionsSlice.reducer;
